/**
 * The languages the app ships translations for. Must match `supportedLngs` in
 * `src/i18n.ts` — adding one means a `<lang>.json` per feature namespace, a line
 * there, and a line here.
 *
 * Labels are endonyms (each language written in itself), never translated: a
 * user who landed on the wrong language has to recognize their own in the list.
 */
export const SUPPORTED_LANGUAGES = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' },
  { id: 'pt', label: 'Português' },
] as const;

export type LanguageId = (typeof SUPPORTED_LANGUAGES)[number]['id'];

export function isLanguageId(value: string | undefined): value is LanguageId {
  return SUPPORTED_LANGUAGES.some((lang) => lang.id === value);
}

/**
 * Where an EXPLICIT language choice is stored — written only by the
 * LanguageSwitcher, never by detection. Its own key (not i18next's default
 * `i18nextLng`) on purpose: before the switcher existed, the detector cached
 * whatever it detected under `i18nextLng`, so every value there is an old
 * auto-detection, not a choice, and must not pin the language.
 */
export const LANGUAGE_STORAGE_KEY = 'silia.language';

/**
 * i18next-browser-languagedetector options. An explicit choice wins; without
 * one, the browser/OS language decides on every load. `caches: []` keeps
 * detection from persisting what it found — otherwise the first language ever
 * detected would stay pinned in that browser even if the user never chose it.
 */
export const LANGUAGE_DETECTION = {
  order: ['localStorage', 'navigator'],
  lookupLocalStorage: LANGUAGE_STORAGE_KEY,
  caches: [] as string[],
};

/** Persists an explicit choice. Storage can throw (private mode, blocked site data). */
export function saveLanguageChoice(id: LanguageId): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, id);
  } catch {
    // The language still changes for this session; it just won't be remembered.
  }
}
